import React from 'react';

/**
 * ReviewCycleWidget + ConcentricReviewWheels
 * ─────────────────────────────────────────────────────────────────
 * Two concentric SVG arcs, sharing a centre point:
 *   inner ring = Progress review (amber #D97706)
 *   outer ring = Strategic review (purple #7C3AED)
 *
 * Rendering rules:
 *   - Both review types have a nextDate → both rings render with
 *     proportional fill arcs.
 *   - Only one nextDate set → only that ring renders. The other ring
 *     is absent entirely (not grey, not there).
 *   - Neither set → both rings render as grey #E5E7EB placeholders.
 *   - Overdue (daysRemaining < 0) → full arc in muted red #DC2626.
 *
 * Centre shows the bold number of days remaining. When both rings
 * are active, the centre shows the SMALLER (sooner) value.
 *
 * Below the wheels: "Next Review" label, then a small legend with a
 * coloured dot per actively-rendered ring.
 *
 * Sizes:
 *   compact (used in the Dashboard widget and the Delivery Guru
 *     header)  inner r=33, outer r=50, stroke 9px (~100px diameter).
 *   panel (used at the top of the Delivery Guru review-cycle slide-in)
 *     inner r=45, outer r=65, stroke 10px.
 *
 * Data shape stored in localStorage `guruone_review_cycle`:
 *   {
 *     progress:  { cadence, startDate, nextDate },
 *     strategic: { cadence, startDate, nextDate }
 *   }
 */

const PROGRESS_COLOUR = '#D97706';
const STRATEGIC_COLOUR = '#7C3AED';
const OVERDUE_COLOUR = '#DC2626';
const PLACEHOLDER_COLOUR = '#E5E7EB';
const MS_PER_DAY = 24 * 3600 * 1000;

const CADENCE_FALLBACK_DAYS = {
  monthly: 30,
  quarterly: 91,
  biannual: 182,
  annual: 365,
};

function dayDiff(fromMs, toMs) {
  return Math.ceil((toMs - fromMs) / MS_PER_DAY);
}

function computeWheelData(entry, todayMs) {
  if (!entry || !entry.nextDate) return null;
  const next = new Date(entry.nextDate).getTime();
  if (!isFinite(next)) return null;

  const daysRemaining = dayDiff(todayMs, next);
  const isOverdue = daysRemaining < 0;

  let cycleDays = null;
  if (entry.startDate) {
    const start = new Date(entry.startDate).getTime();
    if (isFinite(start)) cycleDays = Math.max(1, dayDiff(start, next));
  }
  if (!cycleDays) cycleDays = CADENCE_FALLBACK_DAYS[entry.cadence] || 90;

  const elapsed = cycleDays - daysRemaining;
  let progress = elapsed / cycleDays;
  if (isOverdue) progress = 1;
  else if (progress < 0) progress = 0;
  else if (progress > 1) progress = 1;

  return {
    daysRemaining: Math.max(0, daysRemaining),
    progress,
    isOverdue,
  };
}

function readCycle() {
  try {
    const raw = localStorage.getItem('guruone_review_cycle');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === 'object') ? parsed : null;
  } catch { return null; }
}

const SIZE_CONFIG = {
  xs:      { svg: 88,  innerR: 22, outerR: 35, stroke: 6, daysFontSize: 18, daysLabelSize: 8,  titleSize: 10, legendSize: 9  },
  compact: { svg: 120, innerR: 33, outerR: 50, stroke: 9, daysFontSize: 26, daysLabelSize: 11, titleSize: 12, legendSize: 11 },
  panel:   { svg: 150, innerR: 45, outerR: 65, stroke: 10, daysFontSize: 32, daysLabelSize: 12, titleSize: 13, legendSize: 11 },
};

export function ConcentricReviewWheels({ cycle, size = 'compact', onClick, layout = 'vertical' }) {
  const cfg = SIZE_CONFIG[size] || SIZE_CONFIG.compact;
  const cx = cfg.svg / 2;
  const cy = cfg.svg / 2;
  const innerCirc = 2 * Math.PI * cfg.innerR;
  const outerCirc = 2 * Math.PI * cfg.outerR;

  const todayMs = Date.now();
  const progressData = computeWheelData(cycle?.progress, todayMs);
  const strategicData = computeWheelData(cycle?.strategic, todayMs);

  const renderInner = !!progressData;
  const renderOuter = !!strategicData;
  const noData = !renderInner && !renderOuter;

  const innerColour = progressData?.isOverdue ? OVERDUE_COLOUR : PROGRESS_COLOUR;
  const outerColour = strategicData?.isOverdue ? OVERDUE_COLOUR : STRATEGIC_COLOUR;

  // Centre number: smaller days when both, that ring's days when one, blank when none
  let centreDays = null;
  if (renderInner && renderOuter) centreDays = Math.min(progressData.daysRemaining, strategicData.daysRemaining);
  else if (renderInner) centreDays = progressData.daysRemaining;
  else if (renderOuter) centreDays = strategicData.daysRemaining;

  const wheelEl = (
    <div style={{ position: 'relative', width: cfg.svg, height: cfg.svg }}>
      <svg width={cfg.svg} height={cfg.svg} viewBox={`0 0 ${cfg.svg} ${cfg.svg}`}>
        {/* Track for outer ring (rendered when strategic is active OR no data at all) */}
        {(renderOuter || noData) && (
          <circle
            cx={cx} cy={cy} r={cfg.outerR}
            fill="none"
            stroke={noData ? PLACEHOLDER_COLOUR : outerColour}
            strokeOpacity={noData ? 0.7 : 0.15}
            strokeWidth={cfg.stroke}
          />
        )}
        {/* Track for inner ring (rendered when progress is active OR no data at all) */}
        {(renderInner || noData) && (
          <circle
            cx={cx} cy={cy} r={cfg.innerR}
            fill="none"
            stroke={noData ? PLACEHOLDER_COLOUR : innerColour}
            strokeOpacity={noData ? 0.7 : 0.15}
            strokeWidth={cfg.stroke}
          />
        )}
        {/* Outer arc (Strategic review) */}
        {renderOuter && (
          <circle
            cx={cx} cy={cy} r={cfg.outerR}
            fill="none"
            stroke={outerColour}
            strokeWidth={cfg.stroke}
            strokeDasharray={`${outerCirc * strategicData.progress} ${outerCirc}`}
            strokeLinecap="round"
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        )}
        {/* Inner arc (Progress review) */}
        {renderInner && (
          <circle
            cx={cx} cy={cy} r={cfg.innerR}
            fill="none"
            stroke={innerColour}
            strokeWidth={cfg.stroke}
            strokeDasharray={`${innerCirc * progressData.progress} ${innerCirc}`}
            strokeLinecap="round"
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        )}
      </svg>
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        pointerEvents: 'none',
      }}>
        {centreDays !== null ? (
          <>
            <div style={{ fontSize: cfg.daysFontSize, fontWeight: 800, color: '#1a1a2e', lineHeight: 1 }}>
              {centreDays}
            </div>
            <div style={{ fontSize: cfg.daysLabelSize, color: '#aaa', marginTop: 2, lineHeight: 1 }}>days</div>
          </>
        ) : (
          <div style={{ fontSize: cfg.daysLabelSize + 2, color: '#aaa', lineHeight: 1 }}>—</div>
        )}
      </div>
    </div>
  );

  const labelEl = (
    <div style={{ fontSize: cfg.titleSize, fontWeight: 700, color: '#1a1a2e', textAlign: 'center', marginTop: 8 }}>
      Next Review
    </div>
  );

  const legendEl = (renderInner || renderOuter) ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
      {renderInner && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: cfg.legendSize, color: '#666' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: innerColour, flexShrink: 0 }} />
          Progress review
        </div>
      )}
      {renderOuter && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: cfg.legendSize, color: '#666' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: outerColour, flexShrink: 0 }} />
          Strategic review
        </div>
      )}
    </div>
  ) : null;

  // Horizontal variant: wheel on the right, label + legend stacked on the
  // left with right-aligned text, dot to the right of each label.
  const horizontalLeftEl = (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.35rem', justifyContent: 'center' }}>
      <div style={{ fontSize: cfg.titleSize, fontWeight: 700, color: '#1a1a2e' }}>Next Review</div>
      {renderInner && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: cfg.legendSize, color: '#666' }}>
          <span>Progress review</span>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: innerColour, flexShrink: 0, display: 'inline-block' }} />
        </div>
      )}
      {renderOuter && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: cfg.legendSize, color: '#666' }}>
          <span>Strategic review</span>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: outerColour, flexShrink: 0, display: 'inline-block' }} />
        </div>
      )}
      {noData && (
        <div style={{ fontSize: cfg.legendSize, color: '#aaa' }}>No dates set</div>
      )}
    </div>
  );

  const isHorizontal = layout === 'horizontal';

  if (onClick) {
    return (
      <button
        onClick={onClick}
        aria-label="Review cycle"
        style={{
          background: 'none', border: 'none', padding: 0,
          cursor: 'pointer', fontFamily: 'inherit', width: 'auto',
          display: 'flex',
          flexDirection: isHorizontal ? 'row' : 'column',
          alignItems: 'center',
          gap: isHorizontal ? '0.75rem' : 0,
        }}
      >
        {isHorizontal ? (
          <>
            {horizontalLeftEl}
            {wheelEl}
          </>
        ) : (
          <>
            {wheelEl}
            {labelEl}
            {legendEl}
          </>
        )}
      </button>
    );
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: isHorizontal ? 'row' : 'column',
      alignItems: 'center',
      gap: isHorizontal ? '0.75rem' : 0,
    }}>
      {isHorizontal ? (
        <>
          {horizontalLeftEl}
          {wheelEl}
        </>
      ) : (
        <>
          {wheelEl}
          {labelEl}
          {legendEl}
        </>
      )}
    </div>
  );
}

export default function ReviewCycleWidget({ compact, onClick }) {
  // The compact variant is the only one the Dashboard uses; the slide-in
  // variant lives inline in DeliveryGuru.js so it can manage its own
  // editor state. The earlier "edit mode" rendering of this file is gone.
  if (!compact) return null;

  const cycle = readCycle();

  return (
    <div style={{
      background: 'white',
      border: '1px solid #eee',
      borderRadius: 12,
      padding: '0.75rem 0.9rem',
      display: 'inline-flex',
      flexShrink: 0,
    }}>
      <ConcentricReviewWheels cycle={cycle} size="compact" onClick={onClick} />
    </div>
  );
}
